// CHG-1289 / ITER-3414 — v2 AccessDenied page.
//
// Rendered by the real ProtectedRoute when the user lacks the required
// permission. Brand-aware via useBrand (CHG-1267); shows the missing
// permission code (useful for ops + the user's own admin). Three CTAs:
//   - Switch organization (visible when activeOrg switch is available)
//   - Use classic dashboard (CHG-1278 v1-host-bridge)
//   - Sign out (useAuth().logout)
//
// Lives OUTSIDE <AppShell> visually — the AccessDeniedPage takes the
// full viewport so users never see partial shell chrome they can't
// interact with. (The ProtectedRoute that renders it is itself inside
// AppShell; AccessDenied chooses its own layout via the same minimal-
// dependency styling pattern as CHG-1277 auth pages.)

import { useNavigate } from "react-router-dom";
import { useBrand } from "../contexts/BrandContext";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { v1ClassicUrlForHostname } from "../lib/v1-host-bridge";

interface Props {
  /** The permission the user lacks; surfaced in copy + data-* attrs */
  missingPermission?: string;
}

export function AccessDeniedPage({ missingPermission }: Props) {
  const { chrome } = useBrand();
  const { logout } = useAuth();
  const orgCtx = useOrg();
  const navigate = useNavigate();
  const v1Url =
    typeof window !== "undefined"
      ? v1ClassicUrlForHostname(window.location.hostname)
      : null;
  // org-switch CTA is visible only when there's somewhere to switch TO;
  // the CHG-1167 OrgProvider stub returns null, so this CTA hides itself
  // in dev. When the real OrgSwitcher CHG lands, this hook picks up the
  // multi-org case automatically.
  const canSwitchOrg = Boolean(orgCtx.org);

  return (
    <main
      data-page="access-denied"
      data-missing-permission={missingPermission ?? ""}
      style={{
        minHeight: "100vh",
        background: chrome.login_bg_color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        data-card="access-denied"
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-medium)",
          padding: "32px 28px",
          width: "100%",
          maxWidth: 480,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={chrome.logo_url}
            alt={`${chrome.display_name} logo`}
            style={{ width: 40, height: 40, objectFit: "contain" }}
          />
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--color-text)",
              fontFamily: "var(--font-wordmark, 'Poppins', sans-serif)",
            }}
          >
            {chrome.wordmark}
          </div>
        </div>

        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, color: "var(--color-text)" }}>
            You don't have access to this page
          </h1>
          <p
            style={{
              marginTop: 8,
              marginBottom: 0,
              fontSize: 14,
              color: "var(--color-text-muted)",
              lineHeight: 1.5,
            }}
          >
            Your account doesn't have the permission required to view this surface. Ask
            your organization admin to grant access, or pick one of the options below.
          </p>
          {missingPermission && (
            <div
              data-role="missing-permission"
              style={{
                marginTop: 12,
                padding: "8px 10px",
                fontSize: 12,
                fontFamily: "var(--font-mono, monospace)",
                background: "var(--color-bg-subtle)",
                color: "var(--color-text-muted)",
                borderRadius: "var(--radius-sm, 6px)",
                wordBreak: "break-all",
              }}
            >
              Missing permission: <strong>{missingPermission}</strong>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {canSwitchOrg && (
            <button
              type="button"
              data-action="switch-org"
              onClick={() => navigate("/")}
              style={primaryButton}
            >
              Switch organization
            </button>
          )}
          {v1Url && (
            <a
              href={v1Url}
              data-action="use-classic"
              style={{ ...secondaryButton, textDecoration: "none", textAlign: "center" }}
            >
              Use classic dashboard
            </a>
          )}
          <button
            type="button"
            data-action="sign-out"
            onClick={() => logout()}
            style={secondaryButton}
          >
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}

const primaryButton: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 500,
  border: "none",
  borderRadius: "var(--radius-md, 8px)",
  background: "var(--color-primary, var(--ag-brand-primary, #6366F1))",
  color: "var(--color-on-primary, #fff)",
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryButton: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 500,
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-md, 8px)",
  background: "transparent",
  color: "var(--color-text)",
  cursor: "pointer",
  fontFamily: "inherit",
};
